// FIX: Add import for React to resolve namespace error.
import React, { useCallback, Dispatch, SetStateAction } from 'react';
import { GameState } from '../../domain/entities';
import { clearAllSaves, getAutosave } from '../../infrastructure/GameStateRepository';
import { createNewGame } from '../../services/GameService';
import { defaultGameData, womenGameData } from '../../data/game-data';
import { migrate } from '../../services/MigrationService';
import { ChallengeService } from '../../services/ChallengeService';
import { GAME_VERSION } from '../../version';
import { mensGameConfig } from '../../data/mens/config';
import { PlayerGenerator } from '../../services/playerGenerator';

type SetGameState = Dispatch<SetStateAction<GameState | { gameStarted: false; }>>;
type SetBoolean = Dispatch<SetStateAction<boolean>>;

interface UseGameSetupProps {
    setGameState: SetGameState;
    setIsLoading: SetBoolean;
    setHasSave: SetBoolean;
    onError?: (message: string) => void;
}

// FIX: Added missing 'config' property to initialGameState.
const initialGameState: Omit<GameState, 'gameStarted' | 'leagues' | 'fixtures' | 'cups' | 'playersForSale' | 'managerHistory' | 'transferHistory' | 'news'> & { gameStarted: false; leagues: {}; fixtures: {}; cups: {}; playersForSale: never[]; managerHistory: never[]; transferHistory: never[]; news: never[]; } = {
    version: GAME_VERSION,
    gameStarted: false,
    season: 0,
    currentWeek: 0,
    playerTeamName: '',
    playerLeagueName: '',
    leagues: {},
    fixtures: {},
    cups: {},
    playersForSale: [],
    gameOver: false,
    managerHistory: [],
    transferHistory: [],
    news: [],
    incomingBids: [],
    freeAgents: [],
    searchHistory: [],
    gameType: 'mens',
    config: mensGameConfig,
};

export const useGameSetup = ({ setGameState, setIsLoading, setHasSave, onError }: UseGameSetupProps) => {

    const startGame = useCallback(async () => {
        setIsLoading(true);
        const autosave = await getAutosave();
        if (autosave) {
            try {
                const migratedState = migrate(autosave);
                if (migratedState.gameStarted) {
                     setGameState(migratedState);
                }
            } catch (e: any) {
                console.error("Failed to load and migrate autosave:", e.message);
                if (onError) {
                    onError(e.message);
                }
            }
        } else {
            const newGame = createNewGame(defaultGameData);
            setGameState(newGame);
        }
        setIsLoading(false);
    }, [setIsLoading, setGameState, onError]);

    const startNewGame = useCallback(async (data: any, gameType: 'mens' | 'womens' = 'mens') => {
        await clearAllSaves();
        setHasSave(false);
        const gameData = data 
            ? data 
            : (gameType === 'womens' ? womenGameData : defaultGameData);
        const newGame = createNewGame(gameData);
        // FIX: Initialize player ID counter for new game.
        PlayerGenerator.initializePlayerIdCounter(newGame);
        setGameState(newGame);
    }, [setHasSave, setGameState]);

    const startChallengeGame = useCallback(async (type: 'great_escape' | 'youth_academy' | 'debt_crisis', gameType: 'mens' | 'womens') => {
        await clearAllSaves();
        setHasSave(false);
        const newGame = await ChallengeService.createChallengeGame(type, gameType);
        // FIX: Initialize player ID counter for new challenge game.
        PlayerGenerator.initializePlayerIdCounter(newGame);
        setGameState(newGame);
    }, [setHasSave, setGameState]);

    const resetAndGoToStart = useCallback(async () => {
        await clearAllSaves();
        setHasSave(false);
        setGameState(initialGameState);
    }, [setHasSave, setGameState]);

    const restartGame = useCallback(async () => {
        await clearAllSaves();
        setGameState(createNewGame(defaultGameData));
    }, [setGameState]);

    const hardResetGame = useCallback(async () => {
        await clearAllSaves();
        localStorage.removeItem('psm_theme');
        localStorage.removeItem('psm_font');
        localStorage.removeItem('psm_fontSize');
        localStorage.removeItem('psm_sound_enabled');
        window.location.reload();
    }, []);

    return {
        startGame,
        startNewGame,
        startChallengeGame,
        resetAndGoToStart,
        restartGame,
        hardResetGame,
    };
};
